import React, { useState, useEffect, useRef } from "react";
import {
  conversationAPI,
  generateSessionId,
  getApiBaseURL,
  handleAPIError,
} from "../services/api";
import TypingIndicator from "./TypingIndicator";
import MessageBubble from "./MessageBubble";
import OptionButton from "./OptionButton";

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [currentOptions, setCurrentOptions] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [isFaqFirstMode, setIsFaqFirstMode] = useState(false);
  const [isTagSelectionMode, setIsTagSelectionMode] = useState(false);
  const [enableTextInput, setEnableTextInput] = useState(false); // ⚠️ CRITICAL: Default to FALSE (disabled by default)
  const [isOtherMode, setIsOtherMode] = useState(false); // NEW: Track "Other" mode
  const [selectedTags, setSelectedTags] = useState([]);
  const [askTicketCreation, setAskTicketCreation] = useState(false);
  const [ticketSummary, setTicketSummary] = useState(null);
  const messagesEndRef = useRef(null);

  // Initialize session on component mount
  useEffect(() => {
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);

    // Start conversation with /start command only once
    const timer = setTimeout(() => {
      handleSendMessage("/start", newSessionId, true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle FAQ selection from backend results
  const handleFAQSelect = (faq) => {
    const faqMessage = faq.answer || "No answer available";

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: "bot",
        content: faqMessage,
        isFAQ: true,
        faqData: faq,
        timestamp: new Date(),
      },
    ]);
  };

  // Handle FAQ close
  const handleFAQClose = () => {
    // Remove the FAQ message from the messages array
    setMessages((prev) => prev.filter((msg) => !msg.isFAQ));
  };

  const handleSendMessage = async (
    message,
    sessionIdToUse = sessionId,
    isInitial = false,
  ) => {
    if (!message.trim() && !isInitial) return;

    const userMessage = message.trim() || "Hi"; // Default to 'Hi' if empty

    // Add user message to chat (except for initial load)
    if (!isInitial) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "user",
          content: userMessage,
          timestamp: new Date(),
        },
      ]);
    }

    setIsLoading(true);
    // Remember the options so they can be restored if the message is rejected
    const optionsBeforeSend = currentOptions;
    setCurrentOptions([]);

    try {
      const response = await conversationAPI.sendMessage(
        userMessage,
        sessionIdToUse,
      );

      // ===== BLOCKED BY THE BACKEND PROFANITY FILTER =====
      // Remove the offending message, alert the user and restore the screen
      // so they can rephrase. Detection lives in backend/app/core/moderation.py.
      if (response.is_blocked) {
        setMessages((prev) => {
          const lastUserIndex = prev.map((m) => m.type).lastIndexOf("user");
          const withoutOffending =
            lastUserIndex === -1
              ? prev
              : [...prev.slice(0, lastUserIndex), ...prev.slice(lastUserIndex + 1)];
          return [
            ...withoutOffending,
            {
              id: Date.now() + 1,
              type: "bot",
              content: `${
                response.message ||
                "Please do not mention sensitive and offensive language."
              } Kindly rephrase your query.`,
              isBlocked: true,
              timestamp: new Date(),
            },
          ];
        });
        setCurrentOptions(optionsBeforeSend);
        return;
      }

      // Check for widget action (minimize/maximize/toggle)
      if (response.widget_action === "toggle") {
        // Call the global toggle function to minimize/maximize the widget
        if (typeof window.toggleChatBotWidget === "function") {
          const success = window.toggleChatBotWidget();
          if (!success) {
            console.warn(
              "Widget toggle function returned false - widget may not be initialized",
            );
          }
        } else if (
          typeof window.ChatBotWidget !== "undefined" &&
          typeof window.ChatBotWidget.toggleMinimize === "function"
        ) {
          // Fallback to direct method call
          window.ChatBotWidget.toggleMinimize();
        } else {
          console.warn("No widget toggle function available");
        }
      }

      // Update FAQ-first mode state
      setIsFaqFirstMode(response.is_faq_first_mode || false);
      setIsTagSelectionMode(response.is_tag_selection_mode || false);
      // ⚠️ CRITICAL: Update enable_text_input from backend response (default to false)
      setEnableTextInput(response.enable_text_input !== undefined ? response.enable_text_input : false);
      setIsOtherMode(response.is_other_mode || false); // NEW: Track "Other" mode
      setSelectedTags(response.selected_tags || []);
      setAskTicketCreation(response.ask_ticket_creation || false);
      setTicketSummary(response.ticket_summary || null);

      // Add bot response to chat with enhanced data - only if not a FAQ list response
      if (!response.needs_faq_list) {
        const botMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: response.message,
          options: response.options || [],
          isFinal: response.is_final || false,
          description: response.description,
          direct_url: response.direct_url,
          // FAQ-related data
          faqResults: response.faq_results || [],
          isFaqResponse: response.is_faq_response || false,
          faqQuery: response.faq_query,
          // Overlap case data
          isOverlapCase: response.is_overlap_case || false,
          showFaqPrompt: response.show_faq_prompt || false,
          // Separate message fields
          needsFaqPrompt: response.needs_faq_prompt || false,
          needsFaqList: response.needs_faq_list || false,
          needsFollowupMessage: response.needs_followup_message || false,
          // FAQ-first mode data
          isFaqFirstMode: response.is_faq_first_mode || false,
          isTagSelectionMode: response.is_tag_selection_mode || false,
          selectedTags: response.selected_tags || [],
          askTicketCreation: response.ask_ticket_creation || false,
          ticketSummary: response.ticket_summary || null,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botMessage]);
      }
      console.log("Response options:", response.options);
      console.log("Setting current options:", response.options || []);
      setCurrentOptions(response.options || []);
      setCurrentPath(response.current_path || []);

      // Handle separate FAQ prompt message
      if (response.needs_faq_prompt) {
        // Send a separate FAQ prompt message
        setTimeout(() => {
          const faqPromptMessage = {
            id: Date.now() + 2,
            type: "bot",
            content: `Would you like to know more about ${response.current_path[0] || "this topic"} from FAQs?`,
            isFaqPrompt: true,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, faqPromptMessage]);
          setCurrentOptions(["Yes", "No"]); // Show Yes/No options
        }, 500); // Small delay to make it feel like separate messages
      }

      // Handle separate FAQ list message
      if (response.needs_faq_list) {
        // Send a separate FAQ list message
        setTimeout(() => {
          const faqListMessage = {
            id: Date.now() + 3,
            type: "bot",
            content: response.message,
            isFaqList: true,
            faqResults: response.faq_results,
            options: response.options || [], // ⚠️ CRITICAL: Include "Other" option after FAQ list
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, faqListMessage]);
          // ⚠️ CRITICAL: Set options from response (includes "Other")
          setCurrentOptions(response.options || []);
        }, 500); // Small delay to make it feel like separate messages
      }

      // Handle separate followup message
      if (response.needs_followup_message) {
        // Send a separate "Is there anything else" message
        setTimeout(() => {
          const followupMessage = {
            id: Date.now() + 4,
            type: "bot",
            content: "Is there anything else I can help you with?",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, followupMessage]);
        }, 500); // Small delay to make it feel like separate messages
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: "bot",
          content: `Sorry, I encountered an error: ${errorMessage}`,
          isError: true,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Strip the "12_" style ids from path segments before showing them
  const formatPath = (path) =>
    path.map((item) => {
      const index = item.indexOf("_");
      return index !== -1 ? item.substring(index + 1) : item;
    });

  // "Other" is not descriptive in the tag list - show a clearer label.
  // The raw value is still what gets sent to the backend.
  const getOptionLabel = (option) => {
    if (
      isTagSelectionMode &&
      typeof option === "string" &&
      option.trim().toLowerCase() === "other"
    ) {
      return "None of these — describe my issue";
    }
    return option;
  };

  const handleOptionClick = (option) => {
    // Handle ticket creation confirmation
    if (askTicketCreation && (option === "Confirm" || option === "Cancel")) {
      handleSendMessage(option);
      return;
    }
    
    // Handle FAQ prompt responses
    if (option === "Yes" || option === "No") {
      // Send the response to handle FAQ prompt
      handleSendMessage(option);
    } else if (isTagSelectionMode && option === "Other") {
      // Handle "Other" option in tag selection
      // If tags are already selected, ignore "Other" and send tags
      if (selectedTags.length > 0) {
        handleSendMessage("SEND_TAGS");
      } else {
        // No tags selected - enable text input for "Other"
        handleSendMessage("Other");
      }
    } else if (isTagSelectionMode) {
      // Handle tag selection - add to selected tags
      handleSendMessage(option);
    } else {
      handleSendMessage(option);
    }
  };
  
  const handleCreateTicket = async () => {
    if (!ticketSummary) return;
    
    try {
      setIsLoading(true);
      // Call ticket creation API
      const response = await fetch(`${getApiBaseURL()}/conversation/tickets/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          domain: ticketSummary.domain,
          sub_cat_head: ticketSummary.sub_cat_head || null,
          sub_category: ticketSummary.sub_category || null,
          query: ticketSummary.query || null,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const successMessage = {
          id: Date.now(),
          type: "bot",
          content: `Ticket created successfully!\n\nTicket ID: ${data.ticket_id}\n\n${data.message}`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, successMessage]);
        setAskTicketCreation(false);
        setTicketSummary(null);
      } else {
        throw new Error(data.detail || "Failed to create ticket");
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now(),
        type: "bot",
        content: `Sorry, I encountered an error creating the ticket: ${error.message}`,
        isError: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMainMenu = async () => {
    try {
      // Store current messages to preserve conversation history
      const currentMessages = [...messages];

      // Clear current options and path
      setCurrentOptions([]);
      setCurrentPath([]);
      
      // Reset FAQ-first mode state to default
      setIsFaqFirstMode(false);
      setIsTagSelectionMode(false);
      setEnableTextInput(false); // Will be overridden by backend response
      setIsOtherMode(false);
      setSelectedTags([]);
      setAskTicketCreation(false);
      setTicketSummary(null);
      setInputMessage("");

      // Add a user message showing we're going to main menu
      const userMessage = {
        id: Date.now(),
        type: "user",
        content: "/start",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);

      // Reset session on backend but keep messages
      await conversationAPI.resetSession(sessionId);

      // Send /start command to get categories
      setTimeout(async () => {
        try {
          console.log("Sending /start command to get categories...");
          await handleSendMessage("/start", sessionId, false);
          console.log("Categories should be loaded now");
        } catch (error) {
          console.error("Error getting categories:", error);
        }
      }, 200);
    } catch (error) {
      console.error("Error going to main menu:", error);
    }
  };

  const handleReset = async () => {
    try {
      await conversationAPI.resetSession(sessionId);
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
      setMessages([]);
      setCurrentOptions([]);
      setCurrentPath([]);
      
      // Reset FAQ-first mode state to default
      setIsFaqFirstMode(false);
      setIsTagSelectionMode(false);
      setEnableTextInput(false); // Will be overridden by backend response
      setIsOtherMode(false);
      setSelectedTags([]);
      setAskTicketCreation(false);
      setTicketSummary(null);
      setInputMessage("");

      // Start new conversation
      handleSendMessage("", newSessionId, true);
    } catch (error) {
      console.error("Error resetting conversation:", error);
    }
  };

  const handleGoBack = async () => {
    try {
      setIsLoading(true);
      setCurrentOptions([]);

      // Call the back navigation API
      const response = await conversationAPI.goBack(sessionId);

      // Add user message showing we went back
      const userMessage = {
        id: Date.now(),
        type: "user",
        content: "⬅️ Back",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Add bot response to chat
      const botMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: response.message,
        options: response.options || [],
        isFinal: response.is_final || false,
        description: response.description,
        direct_url: response.direct_url,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
      setCurrentOptions(response.options || []);
      setCurrentPath(response.current_path || []);
    } catch (error) {
      const errorMessage = handleAPIError(error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: "bot",
          content: `Sorry, I encountered an error going back: ${errorMessage}`,
          isError: true,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateOtherQuery = (query) => {
    const trimmed = query.trim();
    const wordCount = trimmed.length ? trimmed.split(/\s+/).length : 0;
    if (trimmed.length < 5) {
      return {
        isValid: false,
        wordCount,
        message: "Please enter at least 5 characters so I can understand your query.",
      };
    }
    if (wordCount > 50) {
      return {
        isValid: false,
        wordCount,
        message: `⚠️ Your query exceeds 50 words (${wordCount} words). Please shorten your query to 50 words or less.`,
      };
    }
    return { isValid: true, wordCount, message: null };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // ⚠️ CRITICAL: Don't allow submission if input is disabled
    if (!enableTextInput && !isTagSelectionMode) {
      return; // Input disabled - user must select from options
    }
    
    // In tag selection mode with tags selected and no input text, send "done" to view FAQs
    if (isTagSelectionMode && selectedTags.length > 0 && !inputMessage.trim()) {
      handleSendMessage("done");
      return;
    }
    
    // Validate length and word count if in "Other" mode
    if (isOtherMode && inputMessage.trim()) {
      const validation = validateOtherQuery(inputMessage);
      if (!validation.isValid) {
        // Show error message
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            type: "bot",
            content: validation.message,
            isError: true,
            timestamp: new Date(),
          },
        ]);
        return; // Don't send - input stays enabled for retry
      }
    }
    
    // Normal message send (only if input is enabled)
    if (inputMessage.trim() && !isLoading && enableTextInput) {
      handleSendMessage(inputMessage);
      setInputMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="chat-container">
      {/* Messages */}
      <div className="chat-messages scrollbar-hide">
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            currentPath={currentPath}
            onFAQSelect={handleFAQSelect}
            onFAQClose={handleFAQClose}
          />
        ))}

        {/* Loading indicator */}
        {isLoading && <TypingIndicator />}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="chat-input">
        {/* Show selected tags in tag selection mode */}
        {isTagSelectionMode && selectedTags.length > 0 && (
          <div className="mb-2 p-2 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700 font-medium">
              Selected tags: <span className="text-blue-600">{selectedTags.join(", ")}</span>
            </p>
            {isTagSelectionMode && (
              <p className="text-xs text-gray-500 mt-1">
                Select more tags or click Send to view FAQs
              </p>
            )}
          </div>
        )}

        {/* Quick options - path on the right in italics (heading removed) */}
        {currentOptions.length > 0 && (
          <div className="mb-4 space-y-2">
            {currentPath.length > 0 && (
              <p className="options-path">
                Path: {formatPath(currentPath).join(" > ")}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {currentOptions.map((option, index) => (
                <OptionButton
                  key={index}
                  option={option}
                  label={getOptionLabel(option)}
                  onClick={() => handleOptionClick(option)}
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex gap-3">
          <textarea
            value={inputMessage}
            onChange={(e) => {
              // Only allow changes if input is enabled
              if (enableTextInput) {
                setInputMessage(e.target.value);
              }
            }}
            onKeyPress={(e) => {
              // Only allow Enter key if input is enabled
              if (enableTextInput && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder={
              !enableTextInput
                ? "Select from options above..."
                : isOtherMode
                ? "Type your query here (max 50 words)..."
                : isTagSelectionMode && selectedTags.length > 0
                ? "Type 'done' to view FAQs, or select more tags"
                : isTagSelectionMode
                ? "Select tags from options above"
                : askTicketCreation
                ? "Select from options above"
                : "Type your message here..."
            }
            className="input-field flex-1"
            rows="1"
            disabled={isLoading || !enableTextInput} // ⚠️ CRITICAL: Disable based on enableTextInput flag
            style={{
              opacity: enableTextInput ? '1' : '0.6',
              cursor: enableTextInput ? 'text' : 'not-allowed',
              backgroundColor: enableTextInput ? '#ffffff' : '#f3f4f6',
              pointerEvents: enableTextInput ? 'auto' : 'none',
              borderColor: enableTextInput ? '#d1d5db' : '#d1d5db'
            }}
          />
          <button
            type="submit"
            disabled={
              isLoading || 
              (!enableTextInput && selectedTags.length === 0) || // Disabled if input disabled and no tags
              (enableTextInput && !inputMessage.trim() && !(isTagSelectionMode && selectedTags.length > 0)) || // Disabled if input enabled but empty (unless tags selected)
              (isTagSelectionMode && selectedTags.length === 0 && !inputMessage.trim()) // Tag mode requires tags or input
            }
            className="send-button"
          >
            {isTagSelectionMode && selectedTags.length > 0 && !inputMessage.trim()
              ? "View FAQs"
              : isOtherMode && inputMessage.trim()
              ? "Submit Query"
              : "Send"}
          </button>
          <button
            type="button"
            onClick={handleGoBack}
            disabled={isLoading || currentPath.length === 0}
            className="back-button"
            title="Go back one step"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleMainMenu}
            disabled={isLoading}
            className="main-menu-button"
            title="Go to Main Menu (Keep conversation history)"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isLoading}
            className="refresh-button"
            title="Reset Conversation (Clear all history and start fresh)"
          >
            {/* Bin / trash icon */}
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 6V4.5A1.5 1.5 0 0110.5 3h3A1.5 1.5 0 0115 4.5V6"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5.5 6l1 13.5A1.5 1.5 0 008 21h8a1.5 1.5 0 001.5-1.5L18.5 6"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 10.5v6M14 10.5v6"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
